import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import OrganizerPromo from '../components/OrganizerPromo';

const eventData = [
  {
    id: 1,
    title: 'Voltage Festival 2026',
    date: 'July 15, 2026',
    time: '7:00 PM',
    location: 'Central Park, NYC',
    badge: 'LIVE',
    badgeColor: '#FF2D55',
    image: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800&q=80'
  },
  {
    id: 2,
    title: 'Neon Grid Concert',
    date: 'July 22, 2026',
    time: '8:30 PM',
    location: 'Madison Square Garden',
    badge: 'TRENDING',
    badgeColor: '#FFD60A',
    image: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800&q=80'
  },
  {
    id: 3,
    title: 'Phantom Wave Tour',
    date: 'August 5, 2026',
    time: '6:00 PM',
    location: 'Brooklyn Bridge Park',
    badge: 'HOT',
    badgeColor: '#FF2D55',
    image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800&q=80',
    backgroundPosition: 'center 20%',
  },
];

const heroSlides = [
  { image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80' },
  { image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1920&q=80' },
  { image: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=1920&q=80' },
];

const steps = [
  {
    num: '01',
    icon: '📲',
    title: 'Book Your Event',
    desc: 'Browse upcoming events and purchase tickets securely through the Whooppe app in seconds.',
  },
  {
    num: '02',
    icon: '🔬',
    title: 'Register Your Face',
    desc: 'One-time biometric enrollment via the app. Your facial data is encrypted and never shared.',
  },
  {
    num: '03',
    icon: '✅',
    title: 'Walk Right In',
    desc: 'Our scanners identify you in under 0.3 seconds. Zero queues, zero paper. Pure experience.',
  },
];

function Home({ showNotification }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      showNotification('Subscribed! Check your inbox.', 'success');
      setEmail('');
    }
  };

  const handleBuyTickets = (title) => {
    showNotification(`Opening tickets for "${title}"…`, 'info');
  };

  return (
    <main>
      {/* ── Hero ── */}
      <section className="hero">
        <div className="hero-carousel">
          {heroSlides.map((slide, i) => (
            <div key={i} className={`hero-slide ${i === currentSlide ? 'active' : ''}`}>
              <div className="hero-image" style={{ backgroundImage: `url('${slide.image}')` }} />
              <div className="hero-overlay" />
            </div>
          ))}
        </div>

        {/* biometric scan overlay */}
        <div className="hero-scan-overlay">
          <div className="scan-beam" />
        </div>

        {/* corner brackets */}
        <div className="hero-corner tl" />
        <div className="hero-corner tr" />
        <div className="hero-corner bl" />
        <div className="hero-corner br" />

        {/* status badge */}
        <div className="hero-scan-status">BIOMETRIC SCAN ACTIVE</div>

        <div className="hero-content">
          <p className="hero-label">Next-Gen Event Access</p>
          <h1 className="hero-title">
            Your Face<br />
            Is Your <span>Ticket</span>
          </h1>
          <p className="hero-subtitle">No queues · No paper · No friction</p>
          <div className="hero-cta-row">
            <a href="https://play.google.com/store/apps/details?id=com.thrillathon.whooppeapp" target="_blank" rel="noopener noreferrer" className="btn-hero-primary">
              <i className="fab fa-google-play" /> Download App
            </a>
            <Link to="/shows" className="btn-hero-outline">
              Browse Events <i className="fas fa-arrow-right" />
            </Link>
          </div>
        </div>

        <button className="carousel-control prev" onClick={prevSlide} aria-label="Previous">
          <i className="fas fa-chevron-left" />
        </button>
        <button className="carousel-control next" onClick={nextSlide} aria-label="Next">
          <i className="fas fa-chevron-right" />
        </button>

        <div className="carousel-indicators">
          {heroSlides.map((_, i) => (
            <span
              key={i}
              className={`indicator ${i === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(i)}
            />
          ))}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-eyebrow">
            <span className="eyebrow-line" />
            <span className="eyebrow-text">How It Works</span>
            <span className="eyebrow-line right" />
          </div>
          <h2 className="section-title">
            Three Steps to <span className="highlight">Frictionless Entry</span>
          </h2>

          <div className="steps-grid">
            {steps.map((step) => (
              <div key={step.num} className="step-card">
                <div className="step-number">STEP {step.num}</div>
                <div className="step-icon-wrap">{step.icon}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── App Download ── */}
      <section className="app-download">
        <div className="container">
          <div className="app-content">
            <div className="app-text">
              <span className="app-eyebrow">Whooppe App</span>
              <h2 className="section-title" style={{ textAlign: 'left' }}>
                Experience Events, <span className="highlight">Ticketless.</span>
              </h2>
              <p className="app-description">
                Book concerts and festivals with our revolutionary biometric ticketing. Walk in,
                get verified in 0.3 s, and enjoy the show — no tickets, no hassle.
              </p>
              <div className="app-features">
                <div className="feature-item"><i className="fas fa-bolt" /><span>Instant Entry</span></div>
                <div className="feature-item"><i className="fas fa-shield-alt" /><span>Bank-Level Security</span></div>
                <div className="feature-item"><i className="fas fa-mobile-alt" /><span>All-in-One App</span></div>
              </div>
              <a href="https://play.google.com/store/apps/details?id=com.thrillathon.whooppeapp" target="_blank" rel="noopener noreferrer" className="btn-download">
                <i className="fab fa-google-play" />
                <div className="btn-text">
                  <span className="btn-small">GET IT ON</span>
                  <span className="btn-large">Google Play</span>
                </div>
              </a>
            </div>
            <div className="app-visual">
              <div className="phone-mockup">
                <div
                  className="phone-screen"
                  style={{
                    backgroundImage: `url('/app-screenshot.jpeg')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section
        className="cta-section"
        style={{ height: '380px', backgroundImage: "url('https://images.unsplash.com/photo-1506157786151-b8491531f063?w=1920&q=80')" }}
      >
        <h4 className="cta-title">Ready to Experience Events Differently?</h4>
        <p className="cta-description">Download the Whooppe app and enter any event with just your face.</p>
        <a href="https://play.google.com/store/apps/details?id=com.thrillathon.whooppeapp" target="_blank" rel="noopener noreferrer" className="btn-cta">
          <i className="fab fa-google-play" />
          <div className="btn-text">
            <span className="btn-small">GET IT ON</span>
            <span className="btn-large">Google Play</span>
          </div>
        </a>
      </section>

      {/* ── Events ── */}
      <section className="events">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" style={{ textAlign: 'left', marginBottom: 0 }}>Upcoming Events</h2>
            <Link to="/shows" className="btn-view-all">
              View All <i className="fas fa-arrow-right" />
            </Link>
          </div>

          <div className="events-grid">
            {eventData.map((event) => (
              <div key={event.id} className="event-card">
                <div className="event-image" style={{ backgroundImage: `url('${event.image}')`, backgroundPosition: event.backgroundPosition || 'center' }}>
                  <div
                    className="event-badge"
                    style={{ background: event.badgeColor }}
                  >
                    {event.badge}
                  </div>
                </div>
                <div className="event-info">
                  <h3 className="event-title">{event.title}</h3>
                  <div className="event-details">
                    <div className="event-detail">
                      <i className="far fa-calendar" />
                      <span>{event.date}</span>
                    </div>
                    <div className="event-detail">
                      <i className="far fa-clock" />
                      <span>{event.time}</span>
                    </div>
                    <div className="event-detail">
                      <i className="fas fa-map-marker-alt" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  <button className="btn-tickets" onClick={() => handleBuyTickets(event.title)}>
                    Get Tickets
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Facial Recognition Promo ── */}
      <OrganizerPromo />

      {/* ── Newsletter ── */}
      <section className="newsletter">
        <div className="container">
          <div className="newsletter-card">
            <h2 className="newsletter-title">Stay in the Loop</h2>
            <p className="newsletter-text">Get early access to tickets, exclusive presales, and event alerts.</p>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email"
                className="newsletter-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="btn-subscribe">Subscribe</button>
            </form>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <img src="/whooppe-logo.png" alt="Whooppe" style={{ height: 68, width: 'auto', objectFit: 'contain', marginBottom: 14 }} />
              <p className="footer-description">Biometric event ticketing technology. Enter any event with just your face.</p>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li><a href="#/">About Whooppe</a></li>
                <li><a href="#/">Careers</a></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><a href="#/">Press</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">For Organizers</h4>
              <ul className="footer-links">
                <li><a href="#/">List Your Show</a></li>
                <li><a href="#/">Partner with Us</a></li>
                <li><a href="#/">Technology</a></li>
                <li><a href="#/">Pricing</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Legal</h4>
              <ul className="footer-links">
                <li><a href="#/">Privacy Policy</a></li>
                <li><a href="#/">Terms & Conditions</a></li>
                <li><a href="#/">Cookie Policy</a></li>
                <li><a href="#/">Security</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Download the App</h4>
              <p className="footer-app-text">Get the Whooppe app for seamless biometric event entry.</p>
              <a href="https://play.google.com/store/apps/details?id=com.thrillathon.whooppeapp" target="_blank" rel="noopener noreferrer" className="btn-footer-download">
                <i className="fab fa-google-play" />
                <div className="btn-text">
                  <span className="btn-small">GET IT ON</span>
                  <span className="btn-large">Google Play</span>
                </div>
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 Whooppe · Thrillathon Technologies. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </main>
  );
}

export default Home;
